import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { auth } from "../../../firebase";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { ETranslation } from "../../../translations/translation-keys";
import Alert, { EAlertColor } from "../../UI/Alert/Alert";
import Button, { EButtonColor } from "../../UI/Button/Button";
import InputGroup from "../../UI/InputGroup/InputGroup";
import Spinner from "../../UI/Spinner/Spinner";
import classes from "./ChangePasswordDialog.module.scss";

interface IProps {
  onCancel: () => void;
}

const ChangePasswordDialog: React.FC<IProps> = ({ onCancel }) => {
  const { t } = useTranslation();

  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
    }

    const user = auth.currentUser;
    if (!user || !user.email) {
      setError("Käyttäjää ei tunnistettu. Ole hyvä ja kirjaudu uudelleen sisään.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSuccess(true);
    } catch (err: any) {
      console.error("Change password error:", err);
      if (
        err?.code === "auth/wrong-password" ||
        err?.code === "auth/invalid-credential" ||
        err?.code === "auth/invalid-login-credentials"
      ) {
        setError(t(ETranslation.CURRENT_PASSWORD_INCORRECT));
      } else if (err?.code === "auth/weak-password") {
        setError(t(ETranslation.PASSWORD_MIN_LENGTH_MESSAGE));
      } else if (err?.code === "auth/requires-recent-login") {
        setError("Toiminto vaatii uudelleenkirjautumisen. Ole hyvä ja kirjaudu uudelleen sisään.");
      } else if (err?.code === "auth/too-many-requests") {
        setError("Liian monta yritystä. Yritä myöhemmin uudelleen.");
      } else {
        setError(err?.message || "Salasanan vaihtaminen epäonnistui. Yritä uudelleen.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div>
        <Alert color={EAlertColor.SUCCESS} style={{ marginBottom: "1rem" }}>
          {t(ETranslation.PASSWORD_CHANGED_SUCCESS)}
        </Alert>
        <Button onClick={onCancel}>{t(ETranslation.COMMON_CLOSE)}</Button>
      </div>
    );
  }

  const isSubmitDisabled = loading || !currentPassword || !newPassword;

  return (
    <form className={classes.Form} onSubmit={handleSubmit}>
      {error && (
        <Alert color={EAlertColor.DANGER} style={{ marginBottom: "1rem" }}>
          {error}
        </Alert>
      )}

      <div className={classes.Field}>
        <label className={classes.Label} htmlFor="currentPassword">
          {t(ETranslation.CURRENT_PASSWORD)}
        </label>
        <input
          id="currentPassword"
          className={classes.Input}
          type="password"
          placeholder={t(ETranslation.CURRENT_PASSWORD)}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={loading}
          autoComplete="current-password"
        />
      </div>

      <div className={classes.Field}>
        <label className={classes.Label} htmlFor="newPassword">
          {t(ETranslation.NEW_PASSWORD)}
        </label>
        <input
          id="newPassword"
          className={classes.Input}
          type="password"
          placeholder={t(ETranslation.NEW_PASSWORD)}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
          autoComplete="new-password"
        />
      </div>

      <div className={classes.ButtonGroup}>
        {loading ? (
          <Spinner />
        ) : (
          <InputGroup>
            <Button disabled={isSubmitDisabled} type="submit" onClick={handleSubmit}>
              {t(ETranslation.CHANGE_PASSWORD)}
            </Button>
            <Button onClick={onCancel} color={EButtonColor.SECONDARY} type="button">
              {t(ETranslation.COMMON_CANCEL)}
            </Button>
          </InputGroup>
        )}
      </div>
    </form>
  );
};

export default ChangePasswordDialog;
